"use client"

import { fetchCategoryBannerData } from "@/hook/useBannerCategory"
import { toImageSrc } from "@/lib/utils"
import { BannerData } from "@/types/types"
import Image from "next/image"
import { useEffect, useState } from "react"
import BannerWithTracking from "./BannerWithTracking"

type CategoryBannerProps = {
  banner?: BannerData | null
  isMobile: boolean
  className?: string
  page?: string
  position?: string
  autoFetch?: boolean
}

const CategoryBanner = ({
  banner: bannerProp,
  isMobile,
  className = "",
  page = "",
  position = "",
  autoFetch = false,
}: CategoryBannerProps) => {
  const [banner, setBanner] = useState<BannerData | null>(bannerProp || null)

  useEffect(() => {
    if (autoFetch || !bannerProp) {
      const fetchBanner = async () => {
        const fetchedBanner = await fetchCategoryBannerData(page, position)
        setBanner(fetchedBanner)
      }
      fetchBanner()
    } else if (bannerProp) {
      setBanner(bannerProp)
    }
  }, [bannerProp, autoFetch, page, position])

  if (!banner) return null

  const bannerImage = isMobile
    ? banner.attributes.mobileBanner?.data?.attributes?.url || banner.attributes.banner?.data?.attributes?.url
    : banner.attributes.banner?.data?.attributes?.url || banner.attributes.mobileBanner?.data?.attributes?.url

  if (!bannerImage) return null

  const imageUrl = toImageSrc(bannerImage)
  const bannerUrl = banner.attributes.url || undefined
  const bannerName = banner.attributes.name || "Banner"

  // Extract tracking config from common field
  const trackingConfig = banner.attributes.common ? {
    clickUrl: banner.attributes.common.clickUrl,
    impressionHtml: banner.attributes.common.impressionHtml,
    vendorScriptSrc: banner.attributes.common.vendorScriptSrc,
    vendorNoscriptHtml: banner.attributes.common.vendorNoscriptHtml,
  } : undefined

  return (
    <BannerWithTracking
      bannerId={`category-banner-${banner.id}`}
      bannerUrl={bannerUrl}
      trackingConfig={trackingConfig}
      className={`block w-full ${className}`}
    >
      <Image
        src={imageUrl}
        alt={bannerName}
        className="w-full h-auto object-cover transition-opacity duration-300 image-hover"
        width={1160}
        height={100}
        priority
        sizes="100%"
      />
    </BannerWithTracking>
  )
}

export default CategoryBanner
